import React, { useState } from 'react';
import { PlugIcon, CodeIcon, ArrowLeftIcon, ArrowRightIcon, CheckIcon, CopyIcon, RefreshCwIcon, AlertCircleIcon, PlusIcon, TrashIcon } from 'lucide-react';
import { Card, StepHeading, BlockLabel, Field, Helper, Toggle, CheckRow } from '../SettingsUI';
import { PremiumSelect } from '../../PremiumSelect';
type View = 'home' | 'connect' | 'api';
type ApiTab = 'orders' | 'mapping' | 'webhooks' | 'keys' | 'logs';
const SYSTEMS = [{
  name: 'Rental & fleet systems',
  desc: 'Assign vehicles, track availability and returns'
}, {
  name: 'Ordering & POS',
  desc: 'Send orders straight to your till or kitchen'
}, {
  name: 'ERP & accounting',
  desc: 'Keep invoices and stock in sync'
}, {
  name: 'Dispatch & delivery',
  desc: 'Route jobs to drivers and track completion'
}, {
  name: 'Booking & scheduling',
  desc: 'Reserve slots, staff and resources'
}, {
  name: 'Fulfilment & warehouse',
  desc: 'Pick, pack and ship from your own stack'
}];
const EVENTS = ['order.created', 'order.updated', 'order.fulfilled', 'order.cancelled', 'payment.completed', 'payment.refunded'];
const TALAB_STATUSES = ['New', 'In Progress', 'Fulfilled', 'Cancelled'];

/**
 * Deliberately two doors: a plain-language "connect a system" path for
 * merchants, and a technical path their IT team can walk into on purpose.
 * None of this complexity is allowed to leak into the Orders page.
 */
export function IntegrationsApi() {
  const [view, setView] = useState<View>('home');
  const [tab, setTab] = useState<ApiTab>('orders');
  const [connected, setConnected] = useState(true);
  const [webhookUrl, setWebhookUrl] = useState('https://api.easylease.ae/talab/webhook');
  const [secret, setSecret] = useState('whsec_7f2a…');
  const [subscribed, setSubscribed] = useState<string[]>(['order.created', 'order.fulfilled', 'payment.completed']);
  const [mapping, setMapping] = useState<Record<string, string>>({
    Confirmed: 'New',
    'Vehicle Assigned': 'In Progress',
    'Vehicle Delivered': 'Fulfilled',
    Cancelled: 'Cancelled'
  });
  const [sandbox, setSandbox] = useState(false);
  const [copied, setCopied] = useState('');
  const copy = (key: string, value: string) => {
    navigator.clipboard?.writeText(value);
    setCopied(key);
    window.setTimeout(() => setCopied(''), 1600);
  };
  if (view === 'home') return <div className="space-y-5" data-id="element-3824" data-mp="2ndrn5adnfvl9">
        <StepHeading title="Integrations & API" desc="Connect Talab with the systems your business already uses. Send orders to your ordering, dispatch, ERP or fulfilment system and keep everything synchronised automatically." data-id="element-3825" data-mp="2ic287jnb2xrw" />

        {/* Health first — it answers "is this working?" before anything else */}
        <Card data-id="element-3826" data-mp="363r2v6nlnyc0">
          <div className="flex items-start gap-3.5" data-id="element-3827" data-mp="364998w3z7sxn">
            <span className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${connected ? 'bg-success/10' : 'bg-warning/10'}`} data-id="element-3828" data-mp="31b6b0ao97vb4">
              {connected ? <CheckIcon className="w-[18px] h-[18px] text-success" data-id="element-3829" data-mp="3dmqlvb75fnuu" /> : <AlertCircleIcon className="w-[18px] h-[18px] text-warning" data-id="element-3830" data-mp="3ijqthqjncyo2" />}
            </span>
            <div className="min-w-0 flex-1" data-id="element-3831" data-mp="3nkmx03036isy">
              <p className="font-bold text-dark-navy text-sm" data-id="element-3832" data-mp="3nhinqr7rkwvf">
                {connected ? 'Connected' : 'Connection issue'}
              </p>
              <p className="text-xs text-slate-500 mt-1" data-id="element-3833" data-mp="e42yf4tng4v">
                {connected ? 'Last sync 2 minutes ago · 42 orders synced today' : '3 events need attention'}
              </p>
            </div>
            <button onClick={() => {
          setView('api');
          setTab('logs');
        }} className="text-xs font-bold text-primary-indigo hover:text-indigo-700 transition-colors duration-150 ease-out flex-shrink-0" data-id="element-3834" data-mp="a71neqtdjf64">
              View details
            </button>
          </div>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-id="element-3835" data-mp="1budsue1hpo3u">
          <Door icon={PlugIcon} title="Connect a system" desc="For existing business platforms and integrations." cta="Browse systems" onClick={() => setView('connect')} data-id="element-3836" data-mp="1pmllbtlqqswq" />
          <Door icon={CodeIcon} title="Build with API" desc="For your technical team or integration partner." cta="Open API tools" onClick={() => setView('api')} data-id="element-3837" data-mp="xwgtj9vnj3lh" />
        </div>

        <Helper data-id="element-3838" data-mp="11x93648fraia">
          Orders keep working exactly the same for your team whether you process
          them in Talab or in your own system.
        </Helper>

        <div className="flex items-center gap-2" data-id="element-3839" data-mp="2i9xyjd60hodd">
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-300" data-id="element-3840" data-mp="2dcxoys9ye3hi">
            Simulate
          </span>
          <button onClick={() => setConnected(!connected)} className="px-2.5 py-1 rounded-lg text-[11px] font-bold text-slate-400 hover:text-primary-indigo transition-colors duration-150 ease-out" data-id="element-3841" data-mp="2lsn8txadw9dn">
            {connected ? 'Connection issue' : 'Healthy connection'}
          </button>
        </div>
      </div>;
  if (view === 'connect') return <div className="space-y-5" data-id="element-3842" data-mp="34kz98ehh90l5">
        <Back onClick={() => setView('home')} data-id="element-3843" data-mp="34kpr1d2obxq0" />
        <StepHeading title="Connect a system" desc="Choose the kind of system you use. Talab sends every new order to it automatically, and its status updates come back here." data-id="element-3844" data-mp="39jivkfzzgee1" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-3845" data-mp="2zsfwtkuj0bhx">
          {SYSTEMS.map(s => <button key={s.name} className="group text-left p-4 bg-white border border-slate-200 rounded-2xl hover:border-primary-indigo/40 transition-colors duration-150 ease-out" data-id="element-3846" data-mp="3niijtmjl2r9o">
              <p className="text-sm font-bold text-dark-navy" data-id="element-3847" data-mp="5ys2k4fqrczk">{s.name}</p>
              <p className="text-xs text-slate-500 mt-1 leading-snug" data-id="element-3848" data-mp="10qm7r9p9rrs">
                {s.desc}
              </p>
              <span className="inline-flex items-center gap-1 text-xs font-bold text-slate-400 group-hover:text-primary-indigo mt-3 transition-colors duration-150 ease-out" data-id="element-3849" data-mp="3s69uekchz8wj">
                Connect
                <ArrowRightIcon className="w-3.5 h-3.5" data-id="element-3850" data-mp="3j7ofr0a5ce11" />
              </span>
            </button>)}
        </div>
        <Helper data-id="element-3851" data-mp="3eellsr1gj2kb">
          Can’t see your system? Your technical team can connect it directly
          with the Talab API.
        </Helper>
      </div>;

  /* ------------------------------------------------------------- API tooling */
  return <div className="space-y-5" data-id="element-3852" data-mp="30gna5teitk8i">
      <Back onClick={() => setView('home')} data-id="element-3853" data-mp="30eu3wf7tl211" />
      <StepHeading title="Build with API" desc="Send Talab orders into your own system and push status updates back." data-id="element-3854" data-mp="2vh28vcbvozsm" />

      <div className="flex items-center gap-1 overflow-x-auto no-scrollbar border-b border-slate-200" data-id="element-3855" data-mp="1axykin72osaw">
        {([['orders', 'Orders API'], ['mapping', 'Status mapping'], ['webhooks', 'Webhooks'], ['keys', 'Credentials'], ['logs', 'Logs']] as [ApiTab, string][]).map(([id, label]) => <button key={id} onClick={() => setTab(id)} className={`px-3.5 py-2.5 text-sm font-semibold whitespace-nowrap border-b-2 -mb-px transition-colors duration-150 ease-out ${tab === id ? 'border-primary-indigo text-primary-indigo' : 'border-transparent text-slate-500 hover:text-slate-800'}`} data-id="element-3856" data-mp="s9bdhz5c5suo">
            {label}
          </button>)}
      </div>

      {tab === 'orders' && <>
          <Card data-id="element-3857" data-mp="8igejroq7be0">
            <BlockLabel hint="Fired the moment a customer completes an order" data-id="element-3858" data-mp="djmarzhlhgsa">
              order.created
            </BlockLabel>
            <p className="text-xs text-slate-500 leading-relaxed" data-id="element-3859" data-mp="330wbtmhwsv36">
              Send new Talab orders directly to your existing system. Each event
              carries the order ID, customer details, items and quantities,
              prices, payment status, fulfilment and delivery or booking
              information, and the order source.
            </p>
            <div className="mt-4 p-4 rounded-xl bg-dark-navy overflow-x-auto" data-id="element-3860" data-mp="38352gc7b7l24">
              <pre className="text-[11px] leading-relaxed text-slate-300 font-mono" data-id="element-3861" data-mp="3lygziq8xtnm7">
                {`POST  https://api.talab.app/v1/orders
{
  "id": "TLB-1048",
  "source": "Talab Shop",
  "customer": { "name": "Mohammed Ali", "phone": "+9715044…" },
  "items": [
    { "name": "Toyota Prado 2026",
      "variant": "Monthly rental", "qty": 1, "price": 4500 }
  ],
  "total": 4500,
  "currency": "AED",
  "payment": "paid",
  "status": "new",
  "fulfilment": { "type": "delivery", "address": "Business Bay, Dubai" }
}`}
              </pre>
            </div>
          </Card>
          <Card className="space-y-4" data-id="element-3862" data-mp="n9nosqjis2n5">
            <div data-id="element-3863" data-mp="n6jfjer76gpm">
              <BlockLabel hint="Read your live catalogue and availability" data-id="element-3864" data-mp="s7729o8e8be1">
                Catalogue API
              </BlockLabel>
              <p className="text-xs text-slate-500" data-id="element-3865" data-mp="e6wa8hvckqra">
                <span className="font-mono text-slate-700" data-id="element-3866" data-mp="i99et2ycyimh">
                  GET /v1/catalogue
                </span>{' '}
                — items, prices, categories and stock.
              </p>
            </div>
            <div className="h-px bg-slate-100" data-id="element-3867" data-mp="9935zudlu6tn" />
            <div data-id="element-3868" data-mp="99w466yhzo06">
              <BlockLabel hint="Push progress back into Talab" data-id="element-3869" data-mp="9ae9dt351ck8">
                Order Status API
              </BlockLabel>
              <p className="text-xs text-slate-500" data-id="element-3870" data-mp="3vnwadenmjt5t">
                <span className="font-mono text-slate-700" data-id="element-3871" data-mp="3vofi62jk9164">
                  PATCH /v1/orders/:id
                </span>{' '}
                — set <span className="font-mono" data-id="element-3872" data-mp="3mnhmq8n6u1z5">new</span>,{' '}
                <span className="font-mono" data-id="element-3873" data-mp="3mmy6cwd6rln8">in_progress</span>,{' '}
                <span className="font-mono" data-id="element-3874" data-mp="21ynpbok2ubqp">fulfilled</span> or{' '}
                <span className="font-mono" data-id="element-3875" data-mp="26y0be734jes8">cancelled</span>. Talab updates the
                merchant Orders page and the customer’s order status
                automatically.
              </p>
            </div>
          </Card>
          <Card data-id="element-3876" data-mp="1xwk262o1pipl">
            <Toggle label="Sandbox mode" desc="Send test orders without touching live data" checked={sandbox} onChange={setSandbox} data-id="element-3877" data-mp="1xy4v5nupotlc" />
          </Card>
        </>}

      {tab === 'mapping' && <Card className="space-y-4" data-id="element-3878" data-mp="2bxmq6kylp335">
          <BlockLabel hint="No code needed — just pick the Talab equivalent" data-id="element-3879" data-mp="2bwldyd5umaju">
            Map your statuses to Talab
          </BlockLabel>
          <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] gap-x-3 items-center" data-id="element-3880" data-mp="34lsbp9xhaw41">
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 pb-1" data-id="element-3881" data-mp="39cif4hmcgow3">
              Your system
            </p>
            <span data-id="element-3882" data-mp="3iq939g6oaab7" />
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 pb-1" data-id="element-3883" data-mp="3ipra4sfb4c7i">
              Talab
            </p>
            {Object.keys(mapping).map(k => <React.Fragment key={k} data-id="element-3884" data-mp="3sg2dodv2wjdv">
                <div className="mt-3 px-4 py-2.5 bg-slate-50 border border-slate-100 rounded-xl text-sm font-semibold text-dark-navy truncate" data-id="element-3885" data-mp="3sj835u7mdna4">
                  {k}
                </div>
                <ArrowRightIcon className="w-4 h-4 text-slate-300 mt-3" data-id="element-3886" data-mp="2zsq4d5dxi82i" />
                <div className="mt-3" data-id="element-3887" data-mp="2vrwfa8or2n81">
                  <PremiumSelect value={mapping[k]} options={TALAB_STATUSES} onChange={v => setMapping(m => ({
              ...m,
              [k]: v
            }))} className="w-full" data-id="element-3888" data-mp="2vsxoe7m7gzn4" />
                </div>
              </React.Fragment>)}
          </div>
          <Helper data-id="element-3889" data-mp="s2rmtkjy0dz4">
            Anything your system reports that isn’t mapped leaves the Talab
            status unchanged, so nothing moves unexpectedly.
          </Helper>
        </Card>}

      {tab === 'webhooks' && <>
          <Card className="space-y-4" data-id="element-3890" data-mp="113hk6souqcey">
            <Field label="Webhook URL" value={webhookUrl} onChange={setWebhookUrl} placeholder="https://" helper="We POST every subscribed event to this endpoint." data-id="element-3891" data-mp="162awihsejxdj" />
            <Field label="Signing secret" value={secret} onChange={setSecret} helper="Verify the signature header on every request." data-id="element-3892" data-mp="ddn8ieg9qtwa" />
          </Card>
          <Card data-id="element-3893" data-mp="n4zuipfdypue">
            <BlockLabel data-id="element-3894" data-mp="3lsx6pbh1feys">Subscribed events</BlockLabel>
            <div className="space-y-0.5" data-id="element-3895" data-mp="3lsdwkgxmpuvb">
              {EVENTS.map(e => <CheckRow key={e} label={e} checked={subscribed.includes(e)} onChange={v => setSubscribed(s => v ? [...s, e] : s.filter(x => x !== e))} data-id="element-3896" data-mp="3qqynfpqzr273" />)}
            </div>
            <div className="flex items-center gap-2 mt-4 pt-4 border-t border-slate-100" data-id="element-3897" data-mp="34b8b9tod2x73">
              <button className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-slate-200 text-xs font-bold text-slate-700 hover:border-primary-indigo/50 hover:text-primary-indigo transition-colors duration-150 ease-out" data-id="element-3898" data-mp="309msxst25qpi">
                <RefreshCwIcon className="w-3.5 h-3.5" data-id="element-3899" data-mp="2qkv3brbgzihr" />
                Send test event
              </button>
              <button className="px-5 py-2.5 rounded-xl bg-primary-indigo text-white text-xs font-bold hover:bg-indigo-700 transition-colors duration-150 ease-out" data-id="element-3900" data-mp="2lma51xfs38vy">
                Save
              </button>
            </div>
          </Card>
        </>}

      {tab === 'keys' && <>
          <Card data-id="element-3901" data-mp="31b896daqt4p9">
            <BlockLabel hint="Used by your system to call the Talab API" data-id="element-3902" data-mp="31croa0knx9pg">
              API keys
            </BlockLabel>
            <div className="space-y-2.5" data-id="element-3903" data-mp="2sicyh42iags3">
              {[{
            name: 'Production key',
            created: 'Created 2 Sep 2026',
            value: 'tlb_live_••••••••••••4f19'
          }, {
            name: 'Sandbox key',
            created: 'Created 2 Sep 2026',
            value: 'tlb_test_••••••••••••90ab'
          }].map(k => <div key={k.name} className="flex items-center gap-3 p-3.5 rounded-xl bg-slate-50 border border-slate-100" data-id="element-3904" data-mp="29ktwulido0fp">
                  <div className="min-w-0 flex-1" data-id="element-3905" data-mp="7rw22k51ixs4">
                    <p className="text-sm font-bold text-dark-navy" data-id="element-3906" data-mp="7qb6qsawjat7">{k.name}</p>
                    <p className="text-xs font-mono text-slate-500 mt-0.5 truncate" data-id="element-3907" data-mp="bsy3n7mfjlx9">
                      {k.value}
                    </p>
                    <p className="text-[11px] text-slate-400 mt-0.5" data-id="element-3908" data-mp="1sagafy3raqyg">
                      {k.created}
                    </p>
                  </div>
                  <button onClick={() => copy(k.name, k.value)} aria-label={`Copy ${k.name}`} className="p-2 rounded-lg text-slate-400 hover:text-primary-indigo hover:bg-white transition-colors duration-150 ease-out flex-shrink-0" data-id="element-3909" data-mp="2635wg2jabvvk">
                    {copied === k.name ? <CheckIcon className="w-4 h-4 text-success" data-id="element-3910" data-mp="2p0z2hdp7glx6" /> : <CopyIcon className="w-4 h-4" data-id="element-3911" data-mp="2k1m724k9q6iz" />}
                  </button>
                  <button aria-label={`Rotate ${k.name}`} className="p-2 rounded-lg text-slate-400 hover:text-primary-indigo hover:bg-white transition-colors duration-150 ease-out flex-shrink-0" data-id="element-3912" data-mp="ulwg3ovgdjyh">
                    <RefreshCwIcon className="w-4 h-4" data-id="element-3913" data-mp="2prfasreequx2" />
                  </button>
                  <button aria-label={`Revoke ${k.name}`} className="p-2 rounded-lg text-slate-400 hover:text-error hover:bg-white transition-colors duration-150 ease-out flex-shrink-0" data-id="element-3914" data-mp="2up7esjsbu7hm">
                    <TrashIcon className="w-4 h-4" data-id="element-3915" data-mp="38ogmev2gvt9z" />
                  </button>
                </div>)}
            </div>
            <button className="inline-flex items-center gap-1.5 mt-4 px-4 py-2.5 rounded-xl border border-dashed border-slate-300 text-xs font-bold text-slate-600 hover:border-primary-indigo/50 hover:text-primary-indigo transition-colors duration-150 ease-out" data-id="element-3916" data-mp="3dmi1l4wrv8g6">
              <PlusIcon className="w-3.5 h-3.5" data-id="element-3917" data-mp="3reybmpo3c6ql" />
              Create API key
            </button>
          </Card>
          <Helper data-id="element-3918" data-mp="83u7h92wpnc">
            A key is shown once, when it’s created. If it’s lost, rotate it —
            Talab can never show a saved secret again.
          </Helper>
        </>}

      {tab === 'logs' && <Card data-id="element-3919" data-mp="1fqjm356vzo3g">
          <BlockLabel hint="Most recent first" data-id="element-3920" data-mp="1fqtawpg73p7k">Integration logs</BlockLabel>
          <div className="overflow-x-auto -mx-1" data-id="element-3921" data-mp="1bp7w2yk3p0uq">
            <table className="w-full min-w-[420px]" data-id="element-3922" data-mp="1bs3v6q7opvkr">
              <thead data-id="element-3923" data-mp="16ry9dqo3j3al">
                <tr className="border-b border-slate-200" data-id="element-3924" data-mp="16shg0b8aq59b">
                  {['Time', 'Event', 'Order', 'Status'].map(h => <th key={h} className="text-left text-[11px] font-bold uppercase tracking-wider text-slate-400 pb-2 px-1" data-id="element-3925" data-mp="23j08tt1kk493">
                      {h}
                    </th>)}
                  <th data-id="element-3926" data-mp="1pp96b4m99kmv" />
                </tr>
              </thead>
              <tbody data-id="element-3927" data-mp="2n5oacojx5bg0">
                {[['12:42 PM', 'status.updated', 'TLB-1048', 'Success'], ['12:41 PM', 'order.created', 'TLB-1048', 'Delivered'], ['11:58 AM', 'order.created', 'TLB-1047', 'Delivered'], ['11:20 AM', 'payment.completed', 'TLB-1046', 'Failed'], ['10:04 AM', 'order.fulfilled', 'TLB-1041', 'Delivered']].map(([time, event, order, status], i) => {
              const failed = status === 'Failed';
              return <tr key={i} className="border-b border-slate-100 last:border-0" data-id="element-3928" data-mp="1lppa0e5z0xzk" data-mpi={i}>
                      <td className="py-2.5 px-1 text-xs text-slate-500 whitespace-nowrap" data-id="element-3929" data-mp="29i5tmt5cwy0z">
                        {time}
                      </td>
                      <td className="py-2.5 px-1 text-xs font-mono text-slate-700" data-id="element-3930" data-mp="20h7qse6nmwjz">
                        {event}
                      </td>
                      <td className="py-2.5 px-1 text-xs text-slate-600" data-id="element-3931" data-mp="24ggizem1qmdf">
                        #{order}
                      </td>
                      <td className="py-2.5 px-1" data-id="element-3932" data-mp="t34nps4crc2v">
                        <span className={`text-xs font-bold ${failed ? 'text-error' : 'text-success'}`} data-id="element-3933" data-mp="t3waqejvw1hj">
                          {status}
                        </span>
                      </td>
                      <td className="py-2.5 px-1 text-right" data-id="element-3934" data-mp="vhrpgqosa8bx">
                        {failed && <button className="text-xs font-bold text-primary-indigo hover:text-indigo-700 transition-colors duration-150 ease-out" data-id="element-3935" data-mp="qiy6wjun3r4w">
                            Retry
                          </button>}
                      </td>
                    </tr>;
            })}
              </tbody>
            </table>
          </div>
        </Card>}
    </div>;
}
function Door({
  icon: Icon,
  title,
  desc,
  cta,
  onClick
}: {
  icon: React.ElementType;
  title: string;
  desc: string;
  cta: string;
  onClick: () => void;
}) {
  return <button onClick={onClick} className="group text-left p-6 bg-white border border-slate-200 rounded-2xl hover:border-primary-indigo/40 hover:shadow-[0_10px_28px_rgba(79,70,229,0.07)] transition-[border-color,box-shadow] duration-150 ease-out" data-id="element-3936" data-mp="3b95q6gs5sxgl">
      <span className="w-10 h-10 rounded-xl bg-primary-indigo/[0.06] flex items-center justify-center" data-id="element-3937" data-mp="sddlc0u1ji9k">
        <Icon className="w-[18px] h-[18px] text-primary-indigo" strokeWidth={1.75} data-id="element-3938" data-mp="1651xu587c8r2" />
      </span>
      <p className="font-bold text-dark-navy mt-4" data-id="element-3939" data-mp="1g14xn4fkzvau">{title}</p>
      <p className="text-xs text-slate-500 mt-1.5 leading-relaxed" data-id="element-3940" data-mp="1fzacbp0ee168">{desc}</p>
      <span className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-500 group-hover:text-primary-indigo mt-4 transition-colors duration-150 ease-out" data-id="element-3941" data-mp="1b0z5ln5eg6s8">
        {cta}
        <ArrowRightIcon className="w-3.5 h-3.5" data-id="element-3942" data-mp="1kzyd32efhwdn" />
      </span>
    </button>;
}
function Back({
  onClick
}: {
  onClick: () => void;
}) {
  return <button onClick={onClick} className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-primary-indigo transition-colors duration-150 ease-out" data-id="element-3943" data-mp="3qtg5s9k5y6of">
      <ArrowLeftIcon className="w-3.5 h-3.5" data-id="element-3944" data-mp="3cofwamlbk438" />
      Integrations & API
    </button>;
}